import type { FormEvent } from 'react'
import type { Activity } from '@/constants/crm'

import { useState } from 'react'
import Button from '@/components/ui/button'
import Field from '@/components/ui/field'
import FormSection from '@/components/companies/new-company/form-section'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { CompanySelect, ContactSelect, OptionSelect, OwnerSelect } from '@/components/generals/form-selects'
import { OWNERS } from '@/constants/companies'
import { ACTIVITY_TYPES } from '@/constants/crm'
import { apiRequest } from '@/lib/client-api'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

type FormState = {
    type: string
    subject: string
    companyId: string
    contactId: string
    owner: string
    dueDate: string
    notes: string
    completed: boolean
}

function ActivityForm() {
    const today = useCrmStore(state => state.today)
    const defaults = useCrmStore(state => state.dialogDefaults)
    const companies = useCrmStore(state => state.companies)
    const contacts = useCrmStore(state => state.contacts)
    const upsertActivity = useCrmStore(state => state.upsertActivity)
    const closeDialog = useCrmStore(state => state.closeDialog)
    const showNotice = useCrmStore(state => state.showNotice)
    const presetContact = contacts.find(contact => contact.id === defaults.contactId)
    const presetCompanyId = defaults.companyId ?? presetContact?.companyId ?? ''
    const companyOwner = companies.find(company => company.id === presetCompanyId)?.owner
    const [form, setForm] = useState<FormState>({
        type: ACTIVITY_TYPES[0],
        subject: '',
        companyId: presetCompanyId,
        contactId: presetContact?.id ?? '',
        owner: companyOwner && OWNERS.some(owner => owner.name === companyOwner) ? companyOwner : OWNERS[0].name,
        dueDate: today,
        notes: '',
        completed: false
    })
    const [errors, setErrors] = useState<{ subject?: string, companyId?: string }>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    function update<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm(current => ({ ...current, [key]: value }))
    }

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()

        if (submitting) return

        const nextErrors = {
            subject: form.subject.trim() ? undefined : "Describe the activity in a few words.",
            companyId: form.companyId ? undefined : "Choose the company this activity is for."
        }

        setErrors(nextErrors)

        if (nextErrors.subject || nextErrors.companyId) return

        setSubmitting(true)
        setFormError(null)

        const result = await apiRequest<{ activity: Activity }>('/api/activities', 'POST', {
            ...form,
            subject: form.subject.trim(),
            notes: form.notes.trim()
        })

        setSubmitting(false)

        if (!result.ok) {
            setFormError(result.message)

            return
        }

        upsertActivity(result.data.activity)
        closeDialog()
        showNotice('success', form.completed ? "Activity logged." : "Activity scheduled.")
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='flex flex-col' aria-busy={submitting}>
            <DialogHeader>
                <DialogTitle>Log Activity</DialogTitle>
                <DialogDescription>
                    Record a call, email, meeting, task or note against an account.
                </DialogDescription>
            </DialogHeader>

            <FormSection title="Activity">
                <div className='grid gap-4 sm:grid-cols-[160px_minmax(0,1fr)]'>
                    <Field label="Type" htmlFor='activity-type'>
                        <OptionSelect id='activity-type' value={form.type} onChange={value => update('type', value)} options={ACTIVITY_TYPES} />
                    </Field>
                    <Field label="Subject" htmlFor='activity-subject' required error={errors.subject}>
                        <Input
                            id='activity-subject'
                            value={form.subject}
                            onChange={event => {
                                update('subject', event.target.value)
                                if (errors.subject) setErrors(current => ({ ...current, subject: undefined }))
                            }}
                            placeholder="Pricing follow-up"
                            maxLength={160}
                            autoComplete='off'
                            aria-invalid={errors.subject ? true : undefined}
                            aria-describedby={errors.subject ? 'activity-subject-error' : undefined}
                            className='aria-invalid:border-danger'
                            autoFocus
                        />
                    </Field>
                </div>
                <Field label="Notes" htmlFor='activity-notes'>
                    <textarea
                        id='activity-notes'
                        value={form.notes}
                        onChange={event => update('notes', event.target.value)}
                        rows={3}
                        maxLength={2000}
                        placeholder="Anything the team should know"
                        className='border-line-strong bg-secondary text-foreground placeholder:text-subtle focus-visible:border-ring ease-power3-out min-h-[84px] w-full resize-y rounded-lg border px-3 py-2.5 text-[14px] leading-[1.4] outline-none transition-[border-color] duration-150'
                    />
                </Field>
            </FormSection>

            <FormSection title="Account & timing">
                <Field label="Company" htmlFor='activity-company' required error={errors.companyId}>
                    <CompanySelect
                        id='activity-company'
                        value={form.companyId}
                        onChange={value => {
                            setForm(current => ({ ...current, companyId: value, contactId: '' }))
                            if (errors.companyId) setErrors(current => ({ ...current, companyId: undefined }))
                        }}
                    />
                </Field>
                <div className='grid gap-4 sm:grid-cols-2'>
                    <Field label="Contact" htmlFor='activity-contact'>
                        <ContactSelect
                            id='activity-contact'
                            value={form.contactId}
                            companyId={form.companyId}
                            onChange={value => update('contactId', value)}
                        />
                    </Field>
                    <Field label="Due date" htmlFor='activity-date'>
                        <Input
                            id='activity-date'
                            type='date'
                            value={form.dueDate}
                            onChange={event => update('dueDate', event.target.value)}
                            className='tabular-nums'
                        />
                    </Field>
                </div>
                <Field label="Owner" htmlFor='activity-owner'>
                    <OwnerSelect id='activity-owner' value={form.owner} onChange={value => update('owner', value)} />
                </Field>
                <label htmlFor='activity-completed' className='caption-style text-soft flex cursor-pointer items-center gap-2.5 select-none'>
                    <Checkbox
                        id='activity-completed'
                        checked={form.completed}
                        onCheckedChange={checked => update('completed', checked === true)}
                    />
                    This already happened
                </label>
            </FormSection>

            {formError && (
                <div role='alert' className='caption-style text-danger px-6 pb-4'>
                    {formError}
                </div>
            )}

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant='subtle' size='sm'>
                        Cancel
                    </Button>
                </DialogClose>
                <Button variant='primary' size='sm' type='submit' disabled={submitting}>
                    <PlusIcon aria-hidden className='size-3' />
                    {submitting ? "Saving…" : "Save Activity"}
                </Button>
            </DialogFooter>
        </form>
    )
}

export default function ActivityDialog() {
    const open = useCrmStore(state => state.dialog === 'activity')
    const closeDialog = useCrmStore(state => state.closeDialog)

    return (
        <Dialog open={open} onOpenChange={next => !next && closeDialog()}>
            <DialogContent className='max-w-[560px]'>
                <ActivityForm />
            </DialogContent>
        </Dialog>
    )
}
